import React from 'react';
import { Skeleton } from 'primereact/skeleton';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';

/**
 * DynamicLeadDetails — Renders lead detail sections from marketplace API.
 *
 * The API returns structured sections based on LoanTypeField schema:
 * {
 *   loan_type: "Personal Loan",
 *   loan_product: "SBI Personal Loan",
 *   sections: [
 *     {
 *       group: "personal_info",
 *       title: "Personal Information",
 *       masked: true,
 *       fields: [
 *         { key: "full_name", label: "Full Name", value: "R***** K****", type: "text" }
 *       ]
 *     }
 *   ]
 * }
 */
const DynamicLeadDetails = ({ sections = [], loanType, loanProduct, loading = false }) => {
    if (loading) {
        return (
            <div className="grid">
                {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="col-12 md:col-6 mb-3">
                        <Skeleton width="40%" height="0.875rem" className="mb-2" />
                        <Skeleton width="65%" height="1.125rem" />
                    </div>
                ))}
            </div>
        );
    }

    if (!sections || sections.length === 0) {
        return (
            <div className="text-500 text-center py-4">
                <i className="pi pi-info-circle mr-2"></i>
                No lead details available
            </div>
        );
    }

    const formatValue = (value, type) => {
        if (value === null || value === undefined || value === '') return '\u2014';

        switch (type) {
            case 'currency':
                if (typeof value === 'number') {
                    return `\u20B9${value.toLocaleString('en-IN')}`;
                }
                return `\u20B9${value}`;

            case 'percentage':
                return `${value}%`;

            case 'number':
                return typeof value === 'number' ? value.toLocaleString('en-IN') : value;

            case 'date':
                try {
                    return new Date(value).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric'
                    });
                } catch {
                    return value;
                }

            case 'boolean':
                return value === true || value === 'true' || value === 1 ? 'Yes' : 'No';

            case 'dropdown':
                // Handle master value objects { id, name, code }
                if (typeof value === 'object' && value !== null && value.name) return value.name;
                // Handle snake_case enum values
                if (typeof value === 'string') {
                    return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                }
                return String(value);

            case 'phone':
                return String(value);

            case 'email':
                return String(value);

            default:
                if (typeof value === 'object' && value !== null) return JSON.stringify(value);
                return String(value);
        }
    };

    return (
        <div>
            {/* Loan Type Header */}
            {(loanType || loanProduct) && (
                <div className="flex align-items-center gap-2 mb-3">
                    {loanType && (
                        <Tag value={loanType} severity="info" className="text-xs" />
                    )}
                    {loanProduct && (
                        <Tag value={loanProduct} severity="secondary" className="text-xs" />
                    )}
                </div>
            )}

            {/* Sections */}
            {sections.map((section, idx) => (
                <div key={section.group || idx} className="mb-4">
                    {/* Section Header */}
                    <div className="flex align-items-center gap-2 mb-3">
                        <span className="text-600 font-semibold text-sm uppercase">
                            {section.title}
                        </span>
                        {section.masked && (
                            <Tag
                                value="Masked"
                                icon="pi pi-lock"
                                severity="warning"
                                style={{ padding: '0.1rem 0.4rem', fontSize: '0.65rem' }}
                            />
                        )}
                    </div>

                    {/* Fields Grid */}
                    <div className="grid">
                        {section.fields.map((field, fIdx) => (
                            <div key={field.key || fIdx} className="col-12 md:col-6 lg:col-4 mb-2">
                                <div className="text-500 text-xs mb-1">{field.label}</div>
                                <div className="text-900 font-medium text-sm">
                                    {formatValue(field.value, field.type)}
                                </div>
                            </div>
                        ))}
                    </div>

                    {idx < sections.length - 1 && <Divider className="my-2" />}
                </div>
            ))}
        </div>
    );
};

export default DynamicLeadDetails;
